import { Button, Stack, Popover as TamaguiPopover, YStack } from 'tamagui';
import { Info } from '@tamagui/lucide-icons';
import { H5 } from '../Heading';
import { Text } from '../Text';

//Tooltip info button
const TooltipInfoButton = <Button color="$foreground/surface-subdued" circular unstyled icon={Info} />;

export interface PopoverProps {
  /**
   * Test id
   */
  testID?: string;
  /**
   * Children are correctly formatted text body/bodies to support bullet points or single text
   */
  children: string | JSX.Element | JSX.Element[];
  /**
   * Title
   */
  title?: string;
  /**
   * Target Element is the Link or Button target which renders in the component
   */
  targetElement?: JSX.Element | 'info';
  /**
   * Placement of the popover. Defaults to 'bottom'
   */
  placement?: 'top' | 'bottom'; //Restricted for consistent arrow placement and experience
  /**
   * Allow flip enables the popover to move a suitable location if there is not enough space to display
   */
  allowFlip?: boolean;
  /**
   * Hoverable enables tooltip style of behavior
   */
  hoverable?: boolean;
}

export const Popover = ({
  testID,
  children,
  title,
  targetElement,
  placement = 'bottom',
  allowFlip = true,
  hoverable = true,
}: PopoverProps) => {
  return (
    <TamaguiPopover placement={placement} allowFlip={allowFlip} hoverable={hoverable}>
      <TamaguiPopover.Trigger testID={testID || 'popover'} asChild>
        <Stack style={{ display: 'inline' }}>{targetElement === 'info' ? TooltipInfoButton : targetElement}</Stack>
      </TamaguiPopover.Trigger>
      <TamaguiPopover.Content
        enterStyle={{ y: 0, opacity: 0 }}
        exitStyle={{ y: 0, opacity: 0 }}
        padding="$space.xl"
        maxWidth={270}
        backgroundColor="$background/surface-inverse"
        borderRadius="$surface/radius/surface-radius"
        marginHorizontal="$space.xl"
        elevate
        animation={[
          'quick',
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
      >
        <TamaguiPopover.Arrow backgroundColor="$background/surface-inverse" />
        <YStack display="flex" gap="$lg" alignItems="flex-start">
          {title ? <H5 color="$foreground/surface-inverse">{title}</H5> : null}
          {typeof children === 'string' ? (
            <Text color="$foreground/surface-inverse" variant="bodySmall">
              {children}
            </Text>
          ) : (
            children
          )}
        </YStack>
      </TamaguiPopover.Content>
    </TamaguiPopover>
  );
};
